import { useQuery } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import {
  getMyReviewerProductivity,
  getReviewerProductivity,
  listCompletedReviews,
  type CompletedReviewRow,
  type ReviewerProductivitySummary,
} from "../api/endpoints/kyc"
import { useAuth } from "../stores/auth"
import { Button } from "../components/ui/button"
import { DataTable, TableBody, TableHead, TableRow, Td, Th } from "../components/ui/data_table"
import { Drawer } from "../components/ui/drawer"
import { EmptyState } from "../components/ui/empty_state"
import { Input, Select } from "../components/ui/input"
import { StatCard } from "../components/ui/stat_card"
import { StatusPill } from "../components/ui/status_pill"

function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return "-"
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))}m`
  const hours = Math.floor(seconds / 3600)
  const mins = Math.round((seconds % 3600) / 60)
  return `${hours}h ${mins}m`
}

function decisionTone(
  decision?: string,
): "neutral" | "success" | "warning" | "danger" | "info" {
  switch (decision) {
    case "APPROVED":
      return "success"
    case "REJECTED":
      return "danger"
    case "ACTION_REQUIRED":
      return "warning"
    default:
      return "neutral"
  }
}

function averageReviewTime(reviewers: ReviewerProductivitySummary[]) {
  const totals = reviewers.reduce(
    (accumulator, reviewer) => {
      if (reviewer.averageReviewTimeSeconds === null) {
        return accumulator
      }

      return {
        weightedSeconds:
          accumulator.weightedSeconds +
          reviewer.averageReviewTimeSeconds * reviewer.totalReviews,
        reviewCount: accumulator.reviewCount + reviewer.totalReviews,
      }
    },
    { weightedSeconds: 0, reviewCount: 0 },
  )

  return totals.reviewCount > 0 ? Math.round(totals.weightedSeconds / totals.reviewCount) : null
}

function queryErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Something went wrong while loading this screen."
}

export function CompletedReviewsPage() {
  const { user } = useAuth()
  const isManagement = ["SUPER_ADMIN", "ADMIN", "OPS_MANAGER"].includes(user?.role ?? "")

  const [search, setSearch] = useState("")
  const [decision, setDecision] = useState("")
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [reviewerId, setReviewerId] = useState("")
  const [page, setPage] = useState(1)
  const [selectedReview, setSelectedReview] = useState<CompletedReviewRow | null>(null)

  const ownProductivityQuery = useQuery({
    queryKey: ["kyc-productivity", "me"],
    queryFn: getMyReviewerProductivity,
    enabled: Boolean(user) && !isManagement,
  })

  const managementProductivityQuery = useQuery({
    queryKey: ["kyc-productivity", "summary"],
    queryFn: getReviewerProductivity,
    enabled: Boolean(user) && isManagement,
  })

  const reviewsQuery = useQuery({
    queryKey: ["completed-reviews", isManagement ? reviewerId : "own", search, decision, dateFrom, dateTo, page],
    queryFn: () => {
      const params: Record<string, string> = {
        page: String(page),
        per_page: "20",
      }
      if (search) params.search = search
      if (decision) params.decision = decision
      if (dateFrom) params.date_from = dateFrom
      if (dateTo) params.date_to = dateTo
      if (isManagement && reviewerId) params.reviewer_id = reviewerId
      return listCompletedReviews(params)
    },
    enabled: Boolean(user),
  })

  const ownProductivity = ownProductivityQuery.data?.data
  const reviewerSummaries = managementProductivityQuery.data?.data.reviewers ?? []
  const filteredReviewerSummaries = reviewerId
    ? reviewerSummaries.filter((reviewer) => reviewer.reviewerId === reviewerId)
    : reviewerSummaries

  const managementSummary = useMemo(() => {
    const aggregate = filteredReviewerSummaries.reduce(
      (accumulator, reviewer) => ({
        totalReviews: accumulator.totalReviews + reviewer.totalReviews,
        approvedCount: accumulator.approvedCount + reviewer.approvedCount,
        rejectedCount: accumulator.rejectedCount + reviewer.rejectedCount,
      }),
      { totalReviews: 0, approvedCount: 0, rejectedCount: 0 },
    )

    return {
      ...aggregate,
      reviewerCount: filteredReviewerSummaries.length,
      averageReviewTimeSeconds: averageReviewTime(filteredReviewerSummaries),
      approvalRate:
        aggregate.totalReviews > 0
          ? Math.round((aggregate.approvedCount / aggregate.totalReviews) * 100)
          : 0,
    }
  }, [filteredReviewerSummaries])

  const items = reviewsQuery.data?.data ?? []
  const meta = reviewsQuery.data?.meta
  const isLoading =
    reviewsQuery.isLoading || ownProductivityQuery.isLoading || managementProductivityQuery.isLoading
  const errorMessage =
    reviewsQuery.isError
      ? queryErrorMessage(reviewsQuery.error)
      : ownProductivityQuery.isError
        ? queryErrorMessage(ownProductivityQuery.error)
        : managementProductivityQuery.isError
          ? queryErrorMessage(managementProductivityQuery.error)
          : null

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-800">Completed Reviews</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          {isManagement
            ? "Completed KYC decisions across reviewers and teams"
            : "Your KYC review history and productivity"}
        </p>
      </div>

      {errorMessage ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {errorMessage}
        </div>
      ) : null}

      {isManagement ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatCard
            label="Displayed Reviews"
            value={meta?.totalItems ?? 0}
            helper="Matches the current filters"
            tone="blue"
          />
          <StatCard
            label="Reviewers In Scope"
            value={managementSummary.reviewerCount}
            helper={reviewerId ? "Filtered reviewer view" : "All reviewers"}
            tone="slate"
          />
          <StatCard
            label="Approval Rate"
            value={`${managementSummary.approvalRate}%`}
            helper={`${managementSummary.approvedCount} approved, ${managementSummary.rejectedCount} rejected`}
            tone="emerald"
          />
          <StatCard
            label="Avg Review Time"
            value={formatDuration(managementSummary.averageReviewTimeSeconds)}
            helper="Weighted average by completed reviews"
            tone="amber"
          />
        </div>
      ) : ownProductivity ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatCard label="Total Reviews" value={ownProductivity.totalReviews} tone="blue" />
          <StatCard
            label="Approval Rate"
            value={`${ownProductivity.approvalRate}%`}
            helper={`${ownProductivity.approvedCount} approved, ${ownProductivity.rejectedCount} rejected`}
            tone="emerald"
          />
          <StatCard
            label="Avg Review Time"
            value={formatDuration(ownProductivity.averageReviewTimeSeconds)}
            tone="slate"
          />
          <StatCard
            label="This Month"
            value={ownProductivity.reviewsThisMonth}
            helper={`${ownProductivity.reviewsThisWeek} this week`}
            tone="amber"
          />
        </div>
      ) : null}

      <div className="flex items-center gap-3 flex-wrap">
        {isManagement ? (
          <Select
            value={reviewerId}
            onChange={(event) => {
              setReviewerId(event.target.value)
              setPage(1)
            }}
            className="w-56"
          >
            <option value="">All reviewers</option>
            {reviewerSummaries.map((reviewer) => (
              <option key={reviewer.reviewerId} value={reviewer.reviewerId}>
                {reviewer.reviewerName}
              </option>
            ))}
          </Select>
        ) : null}
        <Select
          value={decision}
          onChange={(event) => {
            setDecision(event.target.value)
            setPage(1)
          }}
          className="w-40"
        >
          <option value="">All Decisions</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </Select>
        <Input
          type="date"
          value={dateFrom}
          onChange={(event) => {
            setDateFrom(event.target.value)
            setPage(1)
          }}
          className="w-40"
        />
        <Input
          type="date"
          value={dateTo}
          onChange={(event) => {
            setDateTo(event.target.value)
            setPage(1)
          }}
          className="w-40"
        />
        <Input
          placeholder={isManagement ? "Search client, phone, or client ID..." : "Search client or phone..."}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
            setPage(1)
          }}
          className="w-56"
        />
      </div>

      <section className="bg-white rounded-lg shadow-sm border border-slate-200">
        {isLoading ? (
          <div className="p-8 text-sm text-slate-500">Loading completed reviews...</div>
        ) : items.length === 0 ? (
          <div className="p-8">
            <EmptyState
              title={isManagement ? "No completed reviews match these filters" : "No completed reviews"}
              description={
                isManagement
                  ? "Try a different reviewer, decision, or date range."
                  : "You have not completed any KYC reviews yet."
              }
            />
          </div>
        ) : (
          <>
            <DataTable>
              <TableHead>
                <Th>Client Name</Th>
                <Th>Client ID</Th>
                <Th>Phone</Th>
                {isManagement ? <Th>Reviewer</Th> : null}
                <Th>Decision</Th>
                <Th>Rejection Reason</Th>
                <Th>Reviewed At</Th>
                <Th>Review Duration</Th>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={`${item.clientId}-${item.reviewedAt ?? "pending"}`} onClick={() => setSelectedReview(item)}>
                    <Td className="font-medium">{item.fullName}</Td>
                    <Td className="font-mono text-slate-500">{item.clientId}</Td>
                    <Td>{item.phoneNumber}</Td>
                    {isManagement ? <Td>{item.reviewerName ?? "-"}</Td> : null}
                    <Td>
                      <StatusPill tone={decisionTone(item.decision)}>{item.decision}</StatusPill>
                    </Td>
                    <Td className="max-w-[220px] truncate">{item.rejectionReason ?? "-"}</Td>
                    <Td>{item.reviewedAt ? new Date(item.reviewedAt).toLocaleString() : "-"}</Td>
                    <Td>{formatDuration(item.reviewDurationSeconds)}</Td>
                  </TableRow>
                ))}
              </TableBody>
            </DataTable>

            {meta && meta.totalPages > 1 ? (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                <p className="text-xs text-slate-500">
                  Page {meta.page} of {meta.totalPages} ({meta.totalItems} total)
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((current) => current - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page >= meta.totalPages}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </section>

      <Drawer title="Review Detail" open={!!selectedReview} onClose={() => setSelectedReview(null)}>
        {selectedReview ? (
          <div className="space-y-6">
            <section>
              <h3 className="text-sm font-bold text-slate-700 mb-3">Client Information</h3>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-slate-500">Full Name</span>
                  <p className="font-medium text-slate-800">{selectedReview.fullName}</p>
                </div>
                <div>
                  <span className="text-slate-500">Client ID</span>
                  <p className="font-mono text-slate-800">{selectedReview.clientId}</p>
                </div>
                <div>
                  <span className="text-slate-500">Phone</span>
                  <p className="font-medium text-slate-800">{selectedReview.phoneNumber}</p>
                </div>
                <div>
                  <span className="text-slate-500">Application Date</span>
                  <p className="font-medium text-slate-800">
                    {selectedReview.createdAt ? new Date(selectedReview.createdAt).toLocaleDateString() : "-"}
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-bold text-slate-700 mb-3">Decision</h3>
              <div className="space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">Outcome:</span>
                  <StatusPill tone={decisionTone(selectedReview.decision)}>
                    {selectedReview.decision}
                  </StatusPill>
                </div>
                <div>
                  <span className="text-slate-500">Reviewed At:</span>{" "}
                  <span className="font-medium">
                    {selectedReview.reviewedAt ? new Date(selectedReview.reviewedAt).toLocaleString() : "-"}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Reviewer:</span>{" "}
                  <span className="font-medium">{selectedReview.reviewerName ?? "Current reviewer"}</span>
                </div>
                <div>
                  <span className="text-slate-500">Review Duration:</span>{" "}
                  <span className="font-medium">{formatDuration(selectedReview.reviewDurationSeconds)}</span>
                </div>
                {selectedReview.rejectionReason ? (
                  <div>
                    <span className="text-slate-500">Rejection Reason:</span>
                    <p className="mt-1 p-2 bg-red-50 rounded text-red-700 text-xs">
                      {selectedReview.rejectionReason}
                    </p>
                  </div>
                ) : null}
              </div>
            </section>

            {selectedReview.checklist ? (
              <section>
                <h3 className="text-sm font-bold text-slate-700 mb-3">Verification Checklist</h3>
                <div className="space-y-1.5">
                  {selectedReview.checklist.map((item, index) => (
                    <div
                      key={`${item.item}-${index}`}
                      className="flex items-start gap-2 text-xs p-2 border border-slate-100 rounded"
                    >
                      <span className={item.passed ? "text-emerald-600" : "text-red-500"}>
                        {item.passed ? "\u2713" : "\u2717"}
                      </span>
                      <div>
                        <p className="font-medium text-slate-700">{item.item}</p>
                        {item.note ? <p className="text-slate-500 mt-0.5">{item.note}</p> : null}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        ) : null}
      </Drawer>
    </div>
  )
}
